import { getFirestore, doc, runTransaction, getDoc, collection, getDocs, setDoc, deleteDoc, increment, serverTimestamp } from "firebase/firestore";
import { app } from "../../firebase-config.js";

const db = getFirestore(app);

/**
 * DistributedCounterService
 * 
 * Helper for High-Concurrency Counters (Sharding).
 * Solves the "1 write per second" limit of Firestore documents.
 * 
 * Usage:
 * - increment(docRef, 10) -> Increments distinct shard
 * - getCount(docRef, 10) -> Aggregates all shards
 */
export const DistributedCounterService = {

    /**
     * Increment a distributed counter.
     * @param {DocumentReference} docRef - The parent document (e.g., feed doc)
     * @param {string} collectionName - Name of the subcollection for shards (usually 'shards')
     * @param {number} numShards - Number of shards (default 10)
     */
    async increment(docRef, collectionName = 'shards', numShards = 10) {
        const shardId = Math.floor(Math.random() * numShards).toString();
        const shardRef = doc(docRef, collectionName, shardId);

        // Simple increment (No Transaction needed for simple increment, update is enough)
        // set({count: increment(1)}, {merge: true}) is robust.
        return setDoc(shardRef, { count: increment(1) }, { merge: true });
    },

    /**
     * Decrement a distributed counter.
     */
    async decrement(docRef, collectionName = 'shards', numShards = 10) {
        const shardId = Math.floor(Math.random() * numShards).toString();
        const shardRef = doc(docRef, collectionName, shardId);

        return setDoc(shardRef, { count: increment(-1) }, { merge: true });
    },

    /**
     * Get total count by aggregating all shards.
     * Note: This reads numShards documents. Costly if done frequently on client.
     * Use sparingly or primarily allow server-side aggregation.
     */
    async getCount(docRef, collectionName = 'shards') {
        const querySnapshot = await getDocs(collection(docRef, collectionName));
        let total = 0;
        querySnapshot.forEach(doc => {
            total += doc.data().count || 0;
        });
        return total;
    }
};
